/**
 * useAuditViewTracker — fires per-tab view events to
 * POST /api/audit-views/track so admins can see which sections a
 * comp-audit recipient actually opened (and for how long).
 *
 * Fires:
 *   · on tab-change: leave event for previous tab (with dwell_ms) +
 *     enter event for the new tab
 *   · on unload / pagehide: leave event for the current tab, using
 *     navigator.sendBeacon so it survives the tab closing
 */
import { useEffect, useRef } from 'react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function sendTrack(payload, { beacon = false } = {}) {
  if (!payload?.analysis_id || !payload?.tab) return;
  const body = JSON.stringify(payload);
  const url = `${API_BASE}/audit-views/track`;
  const token = localStorage.getItem('le_token');
  if (beacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
    // Beacons don't carry auth headers — pass token as a query param
    // that the backend can optionally accept (skipped for now since
    // the pagehide dwell for the current tab is best-effort only).
    const blob = new Blob([body], { type: 'application/json' });
    try { navigator.sendBeacon(url, blob); } catch { /* noop */ }
    return;
  }
  try {
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch { /* noop */ }
}

export function useAuditViewTracker(analysisId, tab) {
  const prevRef = useRef({ tab: null, enteredAt: 0 });

  useEffect(() => {
    if (!analysisId || !tab) return undefined;
    const now = Date.now();
    const prev = prevRef.current;

    // 1. Leave-event for the previous tab (if any).
    if (prev.tab && prev.tab !== tab) {
      sendTrack({
        analysis_id: analysisId,
        tab: prev.tab,
        dwell_ms: Math.max(0, now - prev.enteredAt),
        entered_at: new Date(prev.enteredAt).toISOString(),
        left_at: new Date(now).toISOString(),
      });
    }
    // 2. Enter-event for the new tab. dwell_ms=0 marks entry; a later
    // tab-change or unload will emit the matching leave-event.
    sendTrack({
      analysis_id: analysisId,
      tab,
      dwell_ms: 0,
      entered_at: new Date(now).toISOString(),
    });
    prevRef.current = { tab, enteredAt: now };
    return undefined;
  }, [analysisId, tab]);

  useEffect(() => {
    const flush = () => {
      const p = prevRef.current;
      if (!p.tab || !analysisId) return;
      const now = Date.now();
      sendTrack({
        analysis_id: analysisId,
        tab: p.tab,
        dwell_ms: Math.max(0, now - p.enteredAt),
        entered_at: new Date(p.enteredAt).toISOString(),
        left_at: new Date(now).toISOString(),
      }, { beacon: true });
    };
    const onHide = () => { if (document.visibilityState === 'hidden') flush(); };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onHide);
      flush();
    };
  }, [analysisId]);
}
